import type { CashFlowKind } from "@/domain/quant/flow-projection";
import { MonetaryBasis } from "@/domain/quant/types";
import type { FrontierScenario } from "@/features/advice-steps/api/frontier";
import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import {
  chatCardClient,
  chatCardSeed,
  isRecord,
  type ChatCardClient,
  type ChatCardSeed,
} from "@/features/portfolio-analysis/lib/chat-card-client";

export interface CashFlowSeed extends ChatCardSeed {
  kind: CashFlowKind;
  amount: number;
}

export interface CashFlowPortfolio {
  id: string;
  name: string;
}

export interface CashFlowEntry {
  toolCallId: string;
  seed: CashFlowSeed;
  client: ChatCardClient | null;
  portfolio: CashFlowPortfolio | null;
  analysis: CashFlowAnalysis | null;
  handedOverAt: number | null;
  advice: boolean;
}

export type CashFlowAnalysis =
  | "pending"
  | "started"
  | "optimising"
  | "ready"
  | "failed"
  | "unconfirmed"
  | "dropped";

export function openCashFlow(toolCallId: string, seed: CashFlowSeed): void {
  STORE.open({
    toolCallId,
    seed,
    client: null,
    portfolio: null,
    analysis: null,
    handedOverAt: null,
    advice: false,
  });
}

export function cashFlowAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function hasCashFlow(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function useCashFlow(toolCallId: string): CashFlowEntry | null {
  return STORE.useEntry(toolCallId);
}

export function useCashFlowAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function useCashFlowStages(): (toolCallId: string) => ChatCardStage | null {
  return STORE.useStages();
}

export function chooseCashFlowClient(toolCallId: string, client: ChatCardClient): void {
  STORE.update(toolCallId, (entry) =>
    entry.analysis === null ? { ...entry, client, portfolio: null } : entry,
  );
}

export function chooseCashFlowPortfolio(
  toolCallId: string,
  client: ChatCardClient,
  portfolio: CashFlowPortfolio,
): void {
  const known = STORE.read(toolCallId);
  if (
    known === null ||
    known.analysis !== null ||
    (known.portfolio?.id === portfolio.id &&
      known.client?.kind === client.kind &&
      known.client.subjectId === client.subjectId)
  ) {
    return;
  }
  STORE.update(toolCallId, (entry) => ({
    ...entry,
    client,
    portfolio: { id: portfolio.id, name: portfolio.name },
  }));
}

export function cashFlowPhase(entry: CashFlowEntry, now: number): CashFlowAnalysis | null {
  if (entry.analysis !== "pending") return entry.analysis;
  return pendingAt(entry, now) ? "pending" : "dropped";
}

export function cashFlowOptimisable(entry: CashFlowEntry, now: number): boolean {
  const phase = cashFlowPhase(entry, now);
  return phase === null || phase === "dropped" || phase === "failed";
}

export function handOverCashFlow(toolCallId: string): boolean {
  const known = STORE.read(toolCallId);
  if (known === null || known.portfolio === null || !cashFlowOptimisable(known, Date.now())) {
    return false;
  }
  STORE.update(toolCallId, (entry) => ({
    ...entry,
    analysis: "pending",
    handedOverAt: Date.now(),
  }));
  return true;
}

export function usePendingCashFlow(portfolioId: string): CashFlowEntry | null {
  const entries = STORE.useAll();
  return (
    entries.find(
      (entry) =>
        entry.portfolio?.id === portfolioId &&
        entry.analysis === "pending" &&
        pendingAt(entry, Date.now()),
    ) ?? null
  );
}

export function useRunningCashFlow(portfolioId: string): CashFlowEntry | null {
  const entries = STORE.useAll();
  return (
    entries.find(
      (entry) =>
        entry.portfolio?.id === portfolioId &&
        (entry.analysis === "started" || entry.analysis === "optimising"),
    ) ?? null
  );
}

export function claimCashFlowAnalysis(toolCallId: string): boolean {
  if (STORE.read(toolCallId)?.analysis !== "pending") return false;
  STORE.update(toolCallId, (entry) => ({
    ...entry,
    analysis: "started",
    handedOverAt: Date.now(),
  }));
  return true;
}

export function noteCashFlowAnalysis(
  toolCallId: string,
  phase: Exclude<CashFlowAnalysis, "pending" | "dropped">,
): void {
  STORE.update(toolCallId, (entry) =>
    entry.analysis === null || entry.analysis === "dropped" ? entry : { ...entry, analysis: phase },
  );
}

export function startCashFlowAdvice(toolCallId: string): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, advice: true }));
}

export function cashFlowScenario(seed: CashFlowSeed): FrontierScenario {
  return {
    depositAmount: seed.kind === "deposit" ? seed.amount : 0,
    withdrawalAmount: seed.kind === "withdrawal" ? seed.amount : 0,
    reservedAmount: 0,
    monetaryBasis: MonetaryBasis.NOMINAL,
  };
}

export function cashFlowAdviceKey(toolCallId: string): string {
  return `${toolCallId}:advice`;
}

export function pendingUntil(entry: CashFlowEntry): number | null {
  return entry.analysis === "pending" && entry.handedOverAt !== null
    ? entry.handedOverAt + HAND_OVER_WINDOW_MS
    : null;
}

function pendingAt(entry: CashFlowEntry, now: number): boolean {
  const until = pendingUntil(entry);
  return until !== null && now < until;
}

function narrowEntry(value: unknown): CashFlowEntry | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string") return null;
  const seed = seedOf(value.seed);
  if (seed === null) return null;
  const client = chatCardClient(value.client);
  const portfolio = client === null ? null : portfolioOf(value.portfolio);
  const analysis = portfolio === null ? null : analysisOf(value.analysis);
  return {
    toolCallId: value.toolCallId,
    seed,
    client,
    portfolio,
    analysis,
    handedOverAt:
      analysis !== null && typeof value.handedOverAt === "number" ? value.handedOverAt : null,
    advice: analysis === "ready" && value.advice === true,
  };
}

function seedOf(value: unknown): CashFlowSeed | null {
  if (!isRecord(value)) return null;
  const { kind, amount } = value;
  if (kind !== "withdrawal" && kind !== "deposit") return null;
  if (typeof amount !== "number" || !Number.isSafeInteger(amount) || amount <= 0) return null;
  return { ...chatCardSeed(value), kind, amount };
}

function portfolioOf(value: unknown): CashFlowPortfolio | null {
  if (!isRecord(value)) return null;
  const { id, name } = value;
  if (typeof id !== "string" || id === "" || typeof name !== "string") return null;
  return { id, name };
}

function analysisOf(value: unknown): CashFlowAnalysis | null {
  return ANALYSES.find((phase) => phase === value) ?? null;
}

const ANALYSES: readonly CashFlowAnalysis[] = [
  "pending",
  "started",
  "optimising",
  "ready",
  "failed",
  "unconfirmed",
  "dropped",
];

const HAND_OVER_WINDOW_MS = 120_000;

const STORE = toolCallStore<CashFlowEntry>({
  key: "simon.analysis.cashFlow",
  narrow: narrowEntry,
  stage: (entry) => (entry.portfolio === null ? "awaiting" : "done"),
});
